/* eslint-disable */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken, setAuthUser } from '../../redux/reducers/authSlice';
import { authApiCall } from '../../services/http';
import { endpoints } from '../../config';

// Normaliza un input controlado: '' → null para mandar borrado explícito al backend.
const inputToNullable = (v) => {
  if (v == null) return null;
  const t = String(v).trim();
  return t === '' ? null : t;
};

// workspaceId es un entero en BD. '' → null, valor inválido → NaN (rechazamos antes del PUT).
const inputToNullableInt = (v) => {
  if (v == null) return null;
  const t = String(v).trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : NaN;
};

EditUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onSaved: PropTypes.func, // (updatedUser) => void
};

export default function EditUserDialog({ open, onClose, user, onSaved }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [givenName, setGivenName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset al abrir o cuando cambia el user
  useEffect(() => {
    if (!open || !user) return;
    const i = {
      givenName: user.givenName ?? null,
      email: user.email ?? null,
      phoneNumber: user.phoneNumber ?? null,
      workspaceId: user.workspace?.id ?? user.workspaceId ?? null,
    };
    setInitial(i);
    setGivenName(i.givenName ?? '');
    setEmail(i.email ?? '');
    setPhoneNumber(i.phoneNumber ?? '');
    setWorkspaceId(i.workspaceId ?? '');
    setError(null);
  }, [open, user]);

  if (!user) return null;

  const save = async () => {
    if (!initial) return;
    setSaving(true);
    setError(null);

    const nextWorkspaceId = inputToNullableInt(workspaceId);
    if (Number.isNaN(nextWorkspaceId)) {
      setSaving(false);
      setError('Workspace ID debe ser un número entero.');
      return;
    }

    const form = {
      givenName: inputToNullable(givenName),
      email: inputToNullable(email),
      phoneNumber: inputToNullable(phoneNumber),
      workspaceId: nextWorkspaceId,
    };

    const body = {};
    if (form.givenName !== (initial.givenName ?? null)) body.givenName = form.givenName;
    if (form.email !== (initial.email ?? null)) body.email = form.email;
    if (form.phoneNumber !== (initial.phoneNumber ?? null)) body.phoneNumber = form.phoneNumber;
    if (form.workspaceId !== (initial.workspaceId ?? null)) body.workspaceId = form.workspaceId;

    if (Object.keys(body).length === 0) {
      setSaving(false);
      onClose();
      return;
    }

    try {
      const res = await authApiCall({
        method: 'PUT',
        endpoint: endpoints.users,
        path: `/${user.id}`,
        data: body,
        navigate,
        dispatch,
        setAuthUser,
        setAuthToken,
        authToken,
        redirectRoute: null,
      });
      if (onSaved) onSaved({ ...user, ...body, ...(res?.data || {}) });
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const userLabel = user.givenName || user.email || `#${user.id}`;
  const emailChanged = inputToNullable(email) !== (initial?.email ?? null);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Editar usuario — {userLabel}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Nombre"
            value={givenName}
            onChange={(e) => setGivenName(e.target.value)}
            fullWidth
          />

          <TextField
            label="Correo"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          {emailChanged && (
            <Alert severity="warning">
              El correo se actualizará en la BD pero <strong>no se sincroniza con Mailjet</strong>. Si el usuario
              recibe campañas, hay que actualizarlo allí manualmente.
            </Alert>
          )}

          <TextField
            label="Teléfono"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            fullWidth
            placeholder="+34 685344764"
          />

          <TextField
            label="Workspace ID"
            type="number"
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            fullWidth
            inputProps={{ step: 1 }}
            helperText="ID numérico del workspace al que pertenece el usuario. Vacío = sin workspace."
          />

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <LoadingButton onClick={save} variant="contained" loading={saving}>
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
